/* eslint-disable prefer-const */
import Cache from '@ember-data/json-api';
import { dequal } from 'dequal';
import { isArray } from '@ember/array';
import { assert } from '@ember/debug';
import { graphFor, peekGraph } from '@ember-data/graph/-private';

const unloadDeleted = (identifier, storeWrapper) => {
  const store = storeWrapper._store;
  store._instanceCache.unloadRecord(identifier);
  store.recordArrayManager.identifierRemoved(identifier);
};

const attrEquals = (attributeDefinition, oldValue, newValue) => {
  if (attributeDefinition?.options?.embedded) {
    return dequal(oldValue, newValue);
  }

  return oldValue === newValue;
};

const unloadEmbeddedFromDiff = (
  identifier,
  field,
  relationshipData,
  storeWrapper,
  graph,
) => {
  if (!field.options.embedded || field.options.noCascade) {
    return;
  }

  const relationship = graph.get(identifier, field.name);
  const remoteState = relationship.remoteState;

  if (!remoteState) {
    return;
  }

  if (field.kind === 'hasMany') {
    [...remoteState].forEach(member => {
      if (!relationshipData.data.some(rel => rel.id === member.id)) {
        unloadDeleted(member, storeWrapper);
      }
    });
  } else if (remoteState.id && remoteState.id !== relationshipData.data?.id) {
    unloadDeleted(remoteState, storeWrapper);
  }
};

const rollbackEmbedded = (identifier, storeWrapper) => {
  const store = storeWrapper._store;

  if (store.isDestroying || store.isDestroyed) {
    return;
  }

  const record = store.peekRecord(identifier);

  if (!record) {
    return;
  }

  record.eachRelationship((relationshipName, relationship) => {
    if (!relationship.options?.embedded) {
      return;
    }

    const related = record[relationshipName];
    if (isArray(related)) {
      related.forEach(child => {
        if (child?.isDirty) {
          child.rollbackAttributes();
        }
      });
    } else if (related?.isDirty) {
      related.rollbackAttributes();
    }
  });
};

const cleanEmbedded = (identifier, storeWrapper, useLocalState) => {
  const graph = peekGraph(storeWrapper);

  if (!graph) {
    return;
  }

  const fields = storeWrapper.schema.fields(identifier);

  fields.forEach(function (relationshipDefinition) {
    if (
      (relationshipDefinition.isAttribute || !relationshipDefinition.options.cascadeDelete &&
        !relationshipDefinition.options.embedded
      ) || relationshipDefinition.options.noCascade
    ) {
      return;
    }

    const relationshipGraph = graph.get(identifier, relationshipDefinition.name);
    let state;

    if (useLocalState) {
      state = relationshipGraph.localState;
    }

    state ??= relationshipGraph.remoteState;

    if (state) {
      if (relationshipDefinition.kind === 'hasMany') {
        [...state].forEach(member => {
          unloadDeleted(member, storeWrapper);
        });
      } else {
        unloadDeleted(state, storeWrapper);
      }
    }
  });
};

function notifyAttributes(storeWrapper, identifier, keys) {
  if (!keys) {
    storeWrapper.notifyChange(identifier, 'attributes', null);
    return;
  }


  for (const key of keys) {
    storeWrapper.notifyChange(identifier, 'attributes', key);
  }
}

function calculateChangedKeys(cached, updates, fields) {
  const changedKeys = new Set();
  const keys = Object.keys(updates);
  const length = keys.length;
  const localAttrs = cached.localAttrs;

  const original = Object.assign(Object.create(null), cached.remoteAttrs, cached.inflightAttrs);

  for (let i = 0; i < length; i++) {
    const key = keys[i];
    if (!fields.has(key)) {
      continue;
    }

    const value = updates[key];

    // A value in localAttrs means the user has a local change to
    // this attribute. We never override this value when merging
    // updates from the backend so we should not sent a change
    // notification if the server value differs from the original.
    if (localAttrs && localAttrs[key] !== undefined) {
      continue;
    }

    if (!attrEquals(fields.get(key), original[key], value)) {
      changedKeys.add(key);
    }
  }

  return changedKeys;
}

function isRelationship(field) {
  const { kind } = field;
  return kind === 'hasMany' || kind === 'belongsTo' || kind === 'resource' || kind === 'collection';
}

function setupRelationships(
  fields,
  identifier,
  data,
  storeWrapper,
) {

  for (const name in data.relationships) {
    const relationshipData = data.relationships[name];
    const field = fields.get(name);

    if (!relationshipData || !field || !isRelationship(field)) { continue; }

    relationshipData.type = field.type;

    const graph = graphFor(storeWrapper);

    unloadEmbeddedFromDiff(
      identifier,
      field,
      relationshipData,
      storeWrapper,
      graph,
    );

    graph.push({
      op: 'updateRelationship',
      record: identifier,
      field: name,
      value: relationshipData,
    });
  }
}

function patchLocalAttributes(cached, changedRemoteKeys, fields) {
  const { localAttrs, remoteAttrs, inflightAttrs, defaultAttrs, changes } = cached;
  if (!localAttrs) {
    cached.changes = null;
    return false;
  }
  let hasAppliedPatch = false;
  const mutatedKeys = Object.keys(localAttrs);

  for (let i = 0, length = mutatedKeys.length; i < length; i++) {
    let attr = mutatedKeys[i];
    // eslint-disable-next-line unicorn/no-nested-ternary, max-len
    const existing = inflightAttrs && attr in inflightAttrs ? inflightAttrs[attr] : remoteAttrs && attr in remoteAttrs ? remoteAttrs[attr] : undefined;

    if (attrEquals(fields.get(attr), localAttrs[attr], existing)) {
      hasAppliedPatch = true;

      // if the local change is committed, then
      // the remoteKeyChange is no longer relevant
      changedRemoteKeys?.delete(attr);

      delete localAttrs[attr];
      delete changes[attr];
    }

    if (defaultAttrs && attr in defaultAttrs) {
      delete defaultAttrs[attr];
    }
  }

  return hasAppliedPatch;
}

function cacheIsEmpty(cached) {
  return !cached || cached.remoteAttrs === null && cached.inflightAttrs === null && cached.localAttrs === null;
}

function _isEmpty(peeked) {
  if (!peeked) {
    return true;
  }
  const isNew = peeked.isNew;
  const isDeleted = peeked.isDeleted;
  const isEmpty = cacheIsEmpty(peeked);
  return (!isNew || isDeleted) && isEmpty;
}

function recordIsLoaded(cached, filterDeleted = false) {
  if (!cached) {
    return false;
  }
  const isNew = cached.isNew;
  const isEmpty = cacheIsEmpty(cached);

  // if we are new we must consider ourselves loaded
  if (isNew) {
    return !cached.isDeleted;
  }
  // even if we have a past request, if we are now empty we are not loaded
  // typically this is true after an unloadRecord call

  // if we are not empty, not new && we have a fulfilled request then we are loaded
  // we should consider allowing for something to be loaded that is simply "not empty".
  // which is how RecordState currently handles this case; however, RecordState is buggy
  // in that it does not account for unloading.
  return filterDeleted && cached.isDeletionCommitted ? false : !isEmpty;
}

function _isLoading(peeked, storeWrapper, identifier) {
  // TODO refactor things such that the cache is not required to know
  // about isLoading
  const req = storeWrapper._store.getRequestStateService();
  const isLoaded = recordIsLoaded(peeked);
  return !isLoaded &&
  req.getPendingRequestsForRecord(identifier).some(_req => _req.type === 'query');
}

function putOne(
  cache,
  identifiers,
  resource,
) {
  assert(
    `You must include an 'id' for the resource data ${resource.type}`,
    resource.id !== null && resource.id !== undefined && resource.id !== '',
  );
  assert(
    // eslint-disable-next-line max-len
    `Missing Resource Type: received resource data with a type '${resource.type}' but no schema could be found with that name.`,
    cache._capabilities.schema.hasResource({ type: resource.type }),
  );
  let identifier = identifiers.peekRecordIdentifier(resource);

  if (identifier) {
    identifier = identifiers.updateRecordIdentifier(identifier, resource);
  } else {
    identifier = identifiers.getOrCreateRecordIdentifier(resource);
  }
  cache.upsert(identifier, resource, cache._capabilities.hasRecord(identifier));
  // even if the identifier was not "existing" before, it is now
  return identifier;
}

export default class EmberDataCache extends Cache {
  upsert(
    identifier,
    data,
    calculateChanges,
  ) {
    let changedKeys;

    const peeked = this.__safePeek(identifier, false);
    const existed = !!peeked;
    const cached = peeked || this._createCache(identifier);
    const isLoading = _isLoading(peeked, this._capabilities, identifier) || !recordIsLoaded(peeked);
    let isUpdate = !_isEmpty(peeked) && !isLoading;

    if (cached.isNew) {
      cached.isNew = false;
      this._capabilities.notifyChange(identifier, 'identity', null);
      this._capabilities.notifyChange(identifier, 'state', null);
    }

    const fields = this._capabilities.schema.fields(identifier);

    if (calculateChanges && existed && data.attributes) {
      changedKeys = calculateChangedKeys(cached, data.attributes, fields);
    }

    cached.remoteAttrs = Object.assign(
      cached.remoteAttrs || (Object.create(null)),
      data.attributes,
    );

    if (cached.localAttrs && patchLocalAttributes(cached, changedKeys, fields)) {
      this._capabilities.notifyChange(identifier, 'state', null);
    }

    if (!isUpdate) {
      this._capabilities.notifyChange(identifier, 'added', null);
    }

    if (data.id) {
      cached.id = data.id;
    }

    if (data.relationships) {
      setupRelationships(fields, identifier, data, this._capabilities);
    }

    if (changedKeys?.size) {
      notifyAttributes(this._capabilities, identifier, changedKeys);
    }

    return changedKeys;
  }


  // eslint-disable-next-line sonarjs/cognitive-complexity
  didCommit(committedIdentifier, result) {
    const payload = result.content;
    const operation = result.request.op;
    const data = payload && payload.data;

    if (!data) {
      assert(
        // eslint-disable-next-line max-len
        `Your ${committedIdentifier.type} record was saved to the server, but the response does not have an id and no id has been set client side. Records must have ids. Please update the server response to provide an id in the response or generate the id on the client side either before saving the record or while normalizing the response.`,
        committedIdentifier.id,
      );
    }

    const { identifierCache } = this._capabilities;
    const existingId = committedIdentifier.id;
    const identifier =
      operation !== 'deleteRecord' && data
        ? identifierCache.updateRecordIdentifier(committedIdentifier, data)
        : committedIdentifier;

    const cached = this.__peek(identifier, false);
    if (cached.isDeleted) {
      this.__graph.push({
        op: 'deleteRecord',
        record: identifier,
        isNew: false,
      });
      cached.isDeletionCommitted = true;
      this._capabilities.notifyChange(identifier, 'removed', null);
      cleanEmbedded(identifier, this._capabilities, false);
    }

    const fields = this._capabilities.schema.fields(identifier);
    cached.isNew = false;

    let newCanonicalAttributes;
    if (data) {
      if (data.id && !cached.id) {
        cached.id = data.id;
      }

      if (identifier === committedIdentifier && identifier.id !== existingId) {
        this._capabilities.notifyChange(identifier, 'identity', null);
      }

      assert(
        // eslint-disable-next-line max-len
        `Expected the ID received for the primary '${identifier.type}' resource being saved to match the current id '${cached.id}' but received '${identifier.id}'.`,
        identifier.id === cached.id,
      );

      if (data.relationships) {
        setupRelationships(fields, identifier, data, this._capabilities);
      }
      newCanonicalAttributes = data.attributes;
    }

    const changedKeys = newCanonicalAttributes && calculateChangedKeys(cached, newCanonicalAttributes, fields);

    cached.remoteAttrs = Object.assign(
      cached.remoteAttrs || Object.create(null),
      cached.inflightAttrs,
      newCanonicalAttributes,
    );
    cached.inflightAttrs = null;
    patchLocalAttributes(cached, changedKeys, fields);

    if (cached.errors) {
      cached.errors = null;
      this._capabilities.notifyChange(identifier, 'errors', null);
    }

    if (changedKeys?.size) {
      notifyAttributes(this._capabilities, identifier, changedKeys);
    }
    this._capabilities.notifyChange(identifier, 'state', null);


    const included = payload && payload.included;
    if (included) {
      for (let i = 0, length = included.length; i < length; i++) {
        putOne(this, identifierCache, included[i]);
      }
    }

    return {
      data: identifier,
    };
  }

  getAttr(identifier, attr) {
    const cached = this.__peek(identifier, true);
    if (!cached) {
      return null;
    }
    return super.getAttr(identifier, attr);
  }

  setAttr(identifier, attr, value) {
    const cached = this.__peek(identifier, false);
    // eslint-disable-next-line max-len, unicorn/no-nested-ternary
    const existing = cached.inflightAttrs && attr in cached.inflightAttrs ? cached.inflightAttrs[attr] : cached.remoteAttrs && attr in cached.remoteAttrs ? cached.remoteAttrs[attr] : undefined;

    const attributesDefinition = this._capabilities.schema.fields(identifier);

    if (!attrEquals(attributesDefinition[attr], existing, value)) {
      cached.localAttrs = cached.localAttrs || Object.create(null);
      cached.localAttrs[attr] = value;
      cached.changes = cached.changes || Object.create(null);
      cached.changes[attr] = [existing, value];
    } else if (cached.localAttrs) {
      delete cached.localAttrs[attr];
      delete cached.changes[attr];
    }

    this._capabilities.notifyChange(identifier, 'attributes', attr);
  }

  unloadRecord(identifier) {
    cleanEmbedded(identifier, this._capabilities, true);
    super.unloadRecord(identifier);
  }

  rollbackAttrs(identifier) {
    rollbackEmbedded(identifier, this._capabilities);
    return super.rollbackAttrs(identifier);
  }
}
